<script setup lang="ts">
import { SUPPORTED_MIME_TYPES } from '@/backend/types'
import { FeatureFlag } from '@/modules/App/featureFlags'
import { useFeatureFlags } from '@/modules/App/useFeatureFlags'
import HubPicker from '@/modules/Cases/HubPicker/HubPicker.vue'
import type { LibraryFile } from '@/modules/Library/libraryStore'
import FileUploadMenu from '@/modules/Project/FileUploadMenu.vue'
import { filterFilesByMimeTypes } from '@/modules/Project/useSupportedMimeTypes'
import type { GoogleDriveFile } from '@/modules/Workspaces/KnowledgeHub/Integrations/GoogleDrive/useGoogleDriveConnection'
import { getFilesFromClipboardEvent } from '@/shared/clipboard'
import { toast } from '@/shared/toast'
import { getFilesFromDataTransferEvent } from '@/shared/utils/file'
import AutosizedTextarea from '@/sharedComponents/AutosizedTextarea.vue'
import CaseMessageInputWebSearch from '@/uiKit/CaseMessageInputWebSearch.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconButton from '@/uiKit/IconButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import { computed, nextTick } from 'vue'
import AgentPicker from './AgentPicker.vue'
import CaseAttachment from './CaseAttachment.vue'
import type { CaseInputAttachment } from './types'
import { useAgentPicker } from './useAgentPicker'

/**
 * Renders a form that lets a user submit a new query to a case, along with
 * attachments.
 */

const props = withDefaults(
  defineProps<{
    attachments: CaseInputAttachment[]
    isDroppingFiles?: boolean
    isEditing?: boolean
    placeholder: string
    disabled?: boolean
    /** Useful to lock the Hub picker from making changes - e.g. when starting a case from a Hub */
    lockedHub?: boolean
  }>(),
  {
    lockedHub: false,
  },
)

const emit = defineEmits<{
  submit: []
  'delete-attachment': [attachmentId: string]
  cancel: []
  'import:library': [files: LibraryFile[]]
  'import:computer': [files: File[]]
  'import:google': [files: GoogleDriveFile[]]
}>()

const text = defineModel<string>('text', { required: true })
const selectedAgentId = defineModel<string | undefined>('selectedAgentId', { required: true })
const selectedHubId = defineModel<string | undefined>('selectedHubId', { required: true })

const isHubsActive = useFeatureFlags(FeatureFlag.HUBS)

const webSearch = defineModel<boolean>('webSearch', { required: true })
const onSubmit = (e: Event) => {
  e.preventDefault()

  if (props.disabled) {
    toast.warning('Your previous question is still being answered, please hold on...')
    return
  }

  nextTick(() => emit('submit'))
}

const messageHasContent = computed(() => !!text.value || props.attachments.length > 0)

const { agents } = useAgentPicker()

const onPaste = async (e: ClipboardEvent) => {
  /**
   * First we get the fallback files synchronously, because we want to prevent the default
   * behaviour if the user has pasted files. Then we get files asynchronously, because
   * we want to handle directories in browsers that support it. We need to prevent
   * default and stop propagation *before* calling an async function.
   */
  const fallbackFiles = getFilesFromClipboardEvent(e)
  if (fallbackFiles) {
    e.preventDefault()
    e.stopImmediatePropagation()
  }

  const files = await getFilesFromDataTransferEvent(e)

  if (files) {
    const { supportedFiles, unsupportedMimeTypes } = filterFilesByMimeTypes(
      files.map(({ file }) => file),
      SUPPORTED_MIME_TYPES,
    )
    emit('import:computer', supportedFiles)
    if (unsupportedMimeTypes.length > 0) {
      toast.error(`Unsupported file types: ${unsupportedMimeTypes.join(', ')}`)
    }
  }
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <div
      v-if="attachments.length > 0"
      class="flex flex-wrap gap-1"
      aria-label="New message attachments"
      role="list"
    >
      <CaseAttachment
        v-for="attachment in attachments"
        :key="attachment.uploadId"
        :name="attachment.name"
        :progress="'progress' in attachment ? attachment.progress : null"
        :type="attachment.type"
        :status="attachment.status"
        deletable
        @delete="emit('delete-attachment', attachment.uploadId)"
      />
    </div>
    <form
      class="relative rounded-corner-20 bg-background-gray-subtlest transition-colors hover:bg-background-gray-subtlest-hovered"
      @submit="onSubmit"
    >
      <div
        v-if="isEditing"
        class="m-1 flex flex-row items-center gap-1 rounded-t-corner-16 rounded-b-corner-6 bg-background-gray-subtlest p-2 text-sm-12px-light text-text-subtle"
      >
        <IconSprite
          icon="task"
          variant="transparent"
          size="sm"
          rounded
        />
        Modifying message
        <div class="flex grow flex-row items-center justify-end">
          <ToolTip title="Cancel">
            <IconButton
              icon="close"
              variant="transparent"
              size="sm"
              rounded
              type="button"
              @click="emit('cancel')"
            />
          </ToolTip>
        </div>
      </div>
      <div
        v-if="isDroppingFiles"
        class="absolute inset-0 flex items-center justify-center rounded-corner-20 bg-background-blue-subtle text-sm-12px-default text-text-selected shadow-focus-ring-primary backdrop-blur-[2px]"
      >
        Drop your files to use them in a case
      </div>
      <div class="go-scrollbar max-h-80 overflow-y-auto">
        <AutosizedTextarea
          v-model="text"
          aria-label="Your message"
          autofocus
          mode="chat"
          :placeholder="placeholder"
          class="bg-background-transparent px-4 pt-3 pb-4 text-lg-15px-light outline-hidden"
          @submit="onSubmit"
          @paste="onPaste"
        />
      </div>
      <div class="flex w-full grow items-center justify-start gap-2 p-2">
        <div class="flex flex-1 flex-row items-center gap-2 self-start">
          <FileUploadMenu
            v-slot="{ getTriggerProps, menu }"
            library-picker-confirm-label="Attach"
            :supported-mime-types="SUPPORTED_MIME_TYPES"
            directory
            @import:library="$emit('import:library', $event)"
            @import:computer="$emit('import:computer', $event)"
            @import:google="$emit('import:google', $event)"
          >
            <IconButton
              aria-label="Add attachment"
              variant="transparent"
              rounded
              size="lg"
              icon="plus"
              :active="menu?.open"
              v-bind="getTriggerProps()"
            />
          </FileUploadMenu>
          <CaseMessageInputWebSearch
            :value="webSearch"
            @change="webSearch = $event"
          />
          <AgentPicker
            v-model="selectedAgentId"
            :agents="agents"
          />
          <HubPicker
            v-if="isHubsActive"
            v-model="selectedHubId"
            :disabled="lockedHub"
          />

          <slot name="status" />
        </div>
        <div class="flex grow flex-row items-center justify-end gap-2">
          <DarwinButton
            v-if="isEditing"
            variant="outline"
            size="sm"
            rounded
            @click="emit('cancel')"
          >
            Cancel
          </DarwinButton>
          <IconButton
            aria-label="Send message"
            class="w-12 self-end transition-colors"
            :variant="messageHasContent ? 'black' : 'brand'"
            size="lg"
            icon="arrow-right"
            rounded
            type="submit"
            :disabled="!messageHasContent || disabled"
          />
        </div>
      </div>
    </form>
  </div>
</template>
